import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  ArrowsClockwise,
  CheckCircle,
  Eye,
  Users,
  Trash,
  X,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const UsersTab = ({ roleFilter, setRoleFilter, fetchStats }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const params = roleFilter ? `?role=${roleFilter}` : '';
      const res = await axios.get(`${API}/api/admin/users${params}`);
      setUsers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete ${name} and ALL their content? This cannot be undone.`)) return;
    setDeleting(userId);
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      toast.success(`${name} deleted`);
      fetchUsers();
      if (fetchStats) fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(null); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.post(`${API}/api/admin/users/${userId}/role?role=${newRole}`);
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const viewUser = async (userId) => {
    try {
      const res = await axios.get(`${API}/api/admin/users/${userId}`);
      setSelectedUser(res.data);
    } catch (err) { toast.error('Failed to load user details'); }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.display_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  if (loading) return <div className="text-center py-12 text-slate-500">Loading users...</div>;

  return (
    <div data-testid="users-tab">
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-lg font-semibold text-white">Users ({filtered.length})</h2>
        <div className="flex gap-2">
          {['', 'user', 'creator', 'admin'].map(r => (
            <button key={r} onClick={() => { setRoleFilter(r); setLoading(true); }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                roleFilter === r ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}>
              {r || 'All'}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs ml-auto">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search users..." className="w-full bg-slate-800 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-white text-sm placeholder-slate-600" />
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map(u => (
          <div key={u.id} className="flex items-center gap-4 p-4 bg-slate-900/80 border border-white/5 rounded-xl hover:border-white/10 transition-colors" data-testid={`user-row-${u.id}`}>
            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden shrink-0">
              {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : <Users className="w-5 h-5 text-slate-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-white font-medium truncate">{u.display_name || 'No name'}</p>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                  u.role === 'admin' ? 'bg-blue-500/20 text-blue-400' : u.role === 'creator' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'
                }`}>{u.role}</span>
                {u.is_age_verified && <CheckCircle weight="fill" className="w-3.5 h-3.5 text-green-400" />}
              </div>
              <p className="text-xs text-slate-500 truncate">{u.email} | {u.tokens} tokens | joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => viewUser(u.id)} className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid={`view-user-${u.id}`}>
                <Eye className="w-4 h-4" />
              </button>
              <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}
                className="bg-slate-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white">
                <option value="user">user</option><option value="creator">creator</option><option value="admin">admin</option>
              </select>
              <button onClick={() => handleDelete(u.id, u.display_name || u.email)}
                disabled={deleting === u.id}
                className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50" data-testid={`delete-user-${u.id}`}>
                {deleting === u.id ? <ArrowsClockwise className="w-4 h-4 animate-spin" /> : <Trash className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSelectedUser(null)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="user-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">User Details</h3>
              <button onClick={() => setSelectedUser(null)} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden">
                {selectedUser.avatar_url ? <img src={selectedUser.avatar_url} alt="" className="w-full h-full object-cover" /> : <Users className="w-8 h-8 text-slate-500" />}
              </div>
              <div>
                <p className="text-white font-bold text-lg">{selectedUser.display_name}</p>
                <p className="text-slate-400 text-sm">{selectedUser.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { label: 'Role', value: selectedUser.role },
                { label: 'Tokens', value: selectedUser.tokens },
                { label: 'Age Verified', value: selectedUser.is_age_verified ? 'Yes' : 'No' },
                { label: 'Creator Verified', value: selectedUser.is_creator_verified ? 'Yes' : 'No' },
                { label: 'Content', value: selectedUser.content_count },
                { label: 'Subscribers', value: selectedUser.subscriber_count },
                { label: 'Gender', value: selectedUser.gender || 'N/A' },
              ].map(item => (
                <div key={item.label} className="bg-black/30 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{item.label}</p>
                  <p className="text-white font-semibold">{item.value}</p>
                </div>
              ))}
            </div>
            {selectedUser.bio && (
              <div className="bg-black/30 rounded-lg p-3 mb-4">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Bio</p>
                <p className="text-white text-sm">{selectedUser.bio}</p>
              </div>
            )}
            {selectedUser.active_deal?.title && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
                <p className="text-[10px] text-amber-400 uppercase tracking-wider mb-1">Active Deal</p>
                <p className="text-white font-semibold">{selectedUser.active_deal.title} — {selectedUser.active_deal.revenue_share_pct}%</p>
              </div>
            )}
            <p className="text-xs text-slate-600">Joined: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : 'N/A'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
